import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Loader2, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/core/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { KIOSK_FLOW_STEPS, type DraftIssue, type DraftStep, type KioskLayoutOption } from '../../types/kiosk-flow-types';
import { FlowStepRow } from './FlowStepRow';

interface FlowStepsTableProps {
  steps: DraftStep[];
  layouts: KioskLayoutOption[];
  isLoadingLayouts: boolean;
  layoutsError: string | null;
  // From validateDraft — row issues are shown on their rows
  issues: DraftIssue[];
  disabled?: boolean;
  onAdd: () => void;
  onChange: (id: string, changes: Partial<Omit<DraftStep, 'id'>>) => void;
  onRemove: (id: string) => void;
  onMove: (activeId: string, overId: string) => void;
  onRetryLayouts: () => void;
}

const NO_ISSUES: DraftIssue[] = [];

// Rows only move up and down
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

// Editable flow steps — the row order is the kiosk screen sequence; drag the handle to reorder
export function FlowStepsTable({
  steps,
  layouts,
  isLoadingLayouts,
  layoutsError,
  issues,
  disabled,
  onAdd,
  onChange,
  onRemove,
  onMove,
  onRetryLayouts,
}: FlowStepsTableProps) {
  const [pendingRemove, setPendingRemove] = useState<DraftStep | null>(null);
  // Each step can appear once, so there's nothing left to add once all are used
  const allStepsUsed = steps.length >= KIOSK_FLOW_STEPS.length;
  const issuesByRow = useMemo(() => {
    const byRow: Record<string, DraftIssue[]> = {};
    for (const issue of issues) {
      if (issue.rowId) (byRow[issue.rowId] ??= []).push(issue);
    }
    return byRow;
  }, [issues]);
  const sensors = useSensors(
    // The small distance keeps a plain click on the handle from starting a drag
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onMove(String(active.id), String(over.id));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs">
          {isLoadingLayouts && (
            <span className="flex items-center gap-1.5 text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading layouts…
            </span>
          )}
          {!isLoadingLayouts && layoutsError && (
            <span className="flex items-center gap-2 text-red-600">
              {layoutsError}
              <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={onRetryLayouts}>
                <RefreshCw className="mr-1 h-3 w-3" />
                Retry
              </Button>
            </span>
          )}
          {!isLoadingLayouts && !layoutsError && layouts.length === 0 && (
            <span className="text-amber-600">No XML files uploaded for this operator</span>
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* span keeps the tooltip working while the button is disabled */}
            <span tabIndex={0}>
              <Button size="icon" variant="outline" disabled={disabled || allStepsUsed} aria-label="Add step" onClick={onAdd}>
                <Plus className="h-4 w-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{allStepsUsed ? 'All steps are already in the flow' : 'Add step'}</TooltipContent>
        </Tooltip>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-600">
              <tr>
                <th className="w-8 py-2 pl-3">#</th>
                <th className="w-8 py-2 pl-2">
                  <span className="sr-only">Reorder</span>
                </th>
                <th className="px-3 py-2">Step</th>
                <th className="px-3 py-2">Layout</th>
                <th className="px-3 py-2">Timeout target</th>
                <th className="px-3 py-2">Timeout (s)</th>
                <th className="w-12 px-3 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {steps.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-gray-500">
                    No steps yet — click + to add one
                  </td>
                </tr>
              ) : (
                <SortableContext items={steps.map((row) => row.id)} strategy={verticalListSortingStrategy}>
                  {steps.map((row, index) => (
                    <FlowStepRow
                      key={row.id}
                      index={index + 1}
                      row={row}
                      allRows={steps}
                      layouts={layouts}
                      isLoadingLayouts={isLoadingLayouts}
                      issues={issuesByRow[row.id] ?? NO_ISSUES}
                      disabled={disabled}
                      onChange={(changes) => onChange(row.id, changes)}
                      onRemove={() => setPendingRemove(row)}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </table>
        </DndContext>
      </div>

      <AlertDialog open={pendingRemove !== null} onOpenChange={(open) => !open && setPendingRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete step{pendingRemove?.step ? ` "${pendingRemove.step}"` : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Timeout targets pointing at this step will be cleared. The change is saved when you click Save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (pendingRemove) onRemove(pendingRemove.id);
                setPendingRemove(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
